import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CalendarDays, Clock, User, MapPin, CheckCircle2, Loader2, Phone, Ticket, Bell, Flame, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { InviteFriendButton } from "@/components/InviteFriendButton";
import { DateInputTR } from "@/components/ui/date-picker-tr";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const DAY = ["Paz", "Pzt", "Sal", "Çar", "Per", "Cum", "Cmt"];
const MON = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
const parse = (ds) => { const [y, m, d] = ds.split("-").map(Number); return new Date(y, m - 1, d); };
const dayLabel = (ds) => { const d = parse(ds); return `${DAY[d.getDay()]}, ${d.getDate()} ${MON[d.getMonth()]}`; };

const post = async (url, body) => {
  const res = await fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "İşlem başarısız");
  return data;
};

const ClassCard = ({ c, onPick, onWaitlist }) => {
  const full = c.available <= 0;
  const pct = Math.min(100, (c.booked / c.capacity) * 100);
  return (
    <div
      role="button"
      tabIndex={full ? -1 : 0}
      aria-disabled={full}
      onClick={() => !full && onPick(c)}
      className={`w-full text-left rounded-2xl border p-4 transition-[transform,box-shadow,border-color] duration-200 ${full ? "bg-stone-100 border-stone-200 cursor-default" : "bg-white border-stone-200 hover:border-emerald-500 hover:shadow-md active:scale-[0.99] cursor-pointer"}`}
      data-testid={`public-class-${c.class_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-bold text-lg text-[#1F2A44] flex items-center gap-1.5" style={{ fontFamily: "Manrope" }}>
            <Clock className="w-4 h-4 text-emerald-600" /> {c.start_time} – {c.end_time}
          </p>
          <p className="text-sm text-stone-600 mt-1">{c.branch ? `${c.branch} · ` : ""}{c.class_type || "Grup Dersi"}</p>
          <p className="text-xs text-stone-500 mt-0.5 flex items-center gap-1"><User className="w-3 h-3" /> {c.trainer_name}</p>
        </div>
        <span className={`shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full ${full ? "bg-stone-200 text-stone-600" : c.available <= 1 ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`} data-testid={`public-class-status-${c.class_id}`}>
          {full ? "Dolu" : `${c.available} yer kaldı`}
        </span>
      </div>
      {c.available === 1 && (
        <p className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-red-700 bg-red-50 border border-red-200 rounded-full px-2.5 py-1 animate-pulse" data-testid={`public-class-last-spot-${c.class_id}`}>
          <Flame className="w-3.5 h-3.5" /> Son 1 yer!
        </p>
      )}
      <div className="mt-3 h-1.5 rounded-full bg-stone-100 overflow-hidden">
        <div className={`h-full rounded-full ${full ? "bg-stone-400" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-[11px] text-stone-500 mt-1">{c.booked}/{c.capacity} dolu</p>
      {c.participants?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`public-class-participants-${c.class_id}`}>
          {c.participants.map((p, i) => (
            <span key={i} className="text-[11px] rounded-full bg-stone-100 text-stone-700 px-2 py-0.5">{p}</span>
          ))}
        </div>
      )}
      {full && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onWaitlist(c); }}
          className="mt-3 w-full rounded-xl border border-amber-300 bg-amber-50 text-amber-800 text-sm font-medium py-2 hover:bg-amber-100 transition-colors flex items-center justify-center gap-1.5"
          data-testid={`public-waitlist-btn-${c.class_id}`}>
          <Bell className="w-4 h-4" /> Yer açılırsa haber ver
        </button>
      )}
    </div>
  );
};

const cleanPhone = (v) => {
  let d = (v || "").replace(/\D/g, "");
  if (d.length === 12 && d.startsWith("90")) d = d.slice(2);
  else if (d.length === 11 && d.startsWith("0")) d = d.slice(1);
  return d;
};

const phoneOk = (v) => {
  const d = cleanPhone(v);
  return d.length === 10 && d.startsWith("5");
};

const nameOk = (v) => /^\p{L}{2,}(?:[ '-]\p{L}{2,})*$/u.test((v || "").trim().replace(/\s+/g, " "));

const BookingDialog = ({ cls, cards, token, onClose, onBooked, waitlist = false }) => {
  const okLabel = waitlist ? "Bekleme Listesine Ekle" : "Rezervasyonu Tamamla";
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState(localStorage.getItem("pb_phone") || "");
  const [match, setMatch] = useState(null);
  const [form, setForm] = useState({ name: "", surname: "", payment: "", mp_id: "", gender: "", birth_date: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => { const v = e.target.value; setForm((f) => ({ ...f, [k]: v })); };
  const pkgs = match?.packages || [];
  const options = [
    ...cards.map((c) => ({ v: `card:${c}`, l: c })),
    ...(step === "confirm" && pkgs.length ? [{ v: "package", l: "Paketim var" }] : []),
    { v: "package_request", l: "Paket almak istiyorum", wide: !(step === "confirm" && pkgs.length) },
  ];
  const ready = !!form.payment && (form.payment !== "package" || !!form.mp_id) && (step !== "new" || (!!form.gender && !!form.birth_date));
  const pickPayment = (v) => setForm((f) => ({ ...f, payment: v, mp_id: v === "package" && pkgs.length === 1 ? pkgs[0].mp_id : "" }));

  useEffect(() => { if (cls) { setStep("phone"); setMatch(null); setError(""); setForm({ name: "", surname: "", payment: "", mp_id: "", gender: "", birth_date: "", email: "" }); } }, [cls]);

  const lookup = async () => {
    setError("");
    if (!phoneOk(phone)) return setError("Geçerli bir cep telefonu girin (5xx xxx xx xx).");
    setBusy(true);
    try {
      const formattedPhone = cleanPhone(phone);
      const r = await post(`${API}/public/booking/${token}/lookup`, { phone: formattedPhone });
      setMatch(r);
      setStep(r.found ? "confirm" : "new");
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const submit = async () => {
    if (busy) return; // Çift tıklamayı engelle
    setError("");
    if (step === "new" && (!nameOk(form.name) || !nameOk(form.surname))) return setError("Ad ve soyad en az 2 harf olmalı ve sadece harf içermeli.");
    if (step === "new" && !form.gender) return setError("Lütfen cinsiyet seçin.");
    if (step === "new" && !form.birth_date) return setError("Lütfen doğum tarihinizi girin.");
    if (step === "new" && form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) return setError("Geçerli bir e-posta girin.");
    if (!form.payment) return setError("Lütfen kart veya paket seçin.");
    if (form.payment === "package" && !form.mp_id) return setError("Lütfen kullanmak istediğiniz paketi seçin.");
    
    setBusy(true);
    const formattedPhone = cleanPhone(phone);
    try {
      const body = { 
        class_id: cls.class_id, 
        phone: formattedPhone, 
        payment: form.payment, 
        ...(form.payment === "package" ? { member_package_id: form.mp_id } : {}), 
        ...(step === "new" ? { name: form.name.trim(), surname: form.surname.trim(), gender: form.gender, birth_date: form.birth_date, email: form.email.trim() } : {}) 
      };
      const r = await post(`${API}/public/booking/${token}/${waitlist ? "waitlist" : "book"}`, body);
      localStorage.setItem("pb_phone", formattedPhone);
      onBooked(r);
    } catch (e) { 
      setError(e.message); 
      setBusy(false); // Sadece hata alınırsa tekrar tıklanabilsin
    }
  };

  const Payment = (
    <div>
      <Label>Nasıl geleceksiniz?</Label>
      <div className="grid grid-cols-2 gap-2 mt-1.5">
        {options.map((o) => (
          <button key={o.v} type="button" onClick={() => pickPayment(o.v)}
            className={`${o.wide ? "col-span-2 " : ""}rounded-xl border px-3 py-2.5 text-sm font-medium flex items-center gap-1.5 transition-colors ${form.payment === o.v ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-stone-200 hover:border-stone-400"}`}
            data-testid={`public-book-payment-${o.v.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
            <Ticket className="w-4 h-4" /> {o.l}
          </button>
        ))}
      </div>
      {form.payment === "package" && (
        <div className="mt-2 space-y-1.5" data-testid="public-book-package-list">
          <p className="text-xs text-stone-500">Hangi paketinizi kullanmak istersiniz?</p>
          {pkgs.map((p) => (
            <button key={p.mp_id} type="button" onClick={() => setForm((f) => ({ ...f, mp_id: p.mp_id }))}
              className={`w-full rounded-xl border px-3 py-2 text-left flex items-center justify-between gap-2 transition-colors ${form.mp_id === p.mp_id ? "border-emerald-600 bg-emerald-50" : "border-stone-200 hover:border-stone-400"}`}
              data-testid={`public-book-package-${p.mp_id}`}>
              <span className="text-sm font-medium text-[#1F2A44]">{p.name}{p.end_date && <span className="block text-[11px] font-normal text-stone-500">Son kullanım: {p.end_date.split("-").reverse().join(".")}</span>}</span>
              <span className="shrink-0 text-xs font-bold rounded-full bg-emerald-100 text-emerald-800 px-2 py-1">{p.remaining_sessions} seans kaldı</span>
            </button>
          ))}
        </div>
      )}
      {!form.payment && <p className="text-[11px] text-stone-500 mt-1.5" data-testid="public-book-payment-hint">Devam etmek için bir seçim yapın.</p>}
    </div>
  );

  return (
    <Dialog open={!!cls} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="max-w-md w-full md:w-[94vw] md:max-h-[92vh] overflow-y-auto md:rounded-2xl content-start" onFocusCapture={(e) => { const el = e.target; if (el.tagName === "INPUT" || el.tagName === "TEXTAREA") setTimeout(() => el.scrollIntoView({ block: "center", behavior: "smooth" }), 350); }} data-testid={waitlist ? "public-waitlist-dialog" : "public-booking-dialog"}>
        <DialogHeader><DialogTitle style={{ fontFamily: "Manrope" }}>{waitlist ? "Bekleme Listesi" : "Rezervasyon"}</DialogTitle></DialogHeader>
        {cls && (
          <div className={`rounded-xl border p-3 text-sm ${waitlist ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-emerald-50 border-emerald-100 text-emerald-900"}`} data-testid="public-book-class-info">
            <b>{dayLabel(cls.date)}</b> · {cls.start_time}–{cls.end_time} · {cls.trainer_name}
            {waitlist && <p className="text-xs mt-1">Bu ders dolu. Yer açılırsa stüdyo size WhatsApp'tan haber verecek.</p>}
          </div>
        )}
        {step === "phone" && (
          <>
            <div>
              <Label>Cep telefonunuz</Label>
              <Input type="tel" inputMode="tel" placeholder="05xx xxx xx xx" value={phone} onChange={(e) => setPhone(e.target.value)} className="rounded-xl" data-testid="public-book-phone" />
              <p className="text-[11px] text-stone-500 mt-1">Daha önce geldiyseniz numaranızla sizi tanırız.</p>
            </div>
            {error && <p className="text-sm text-red-600" data-testid="public-book-error">{error}</p>}
            <Button onClick={lookup} disabled={busy} className="w-full h-12 rounded-xl bg-[#1F2A44] hover:bg-[#1F2A44]/90 text-base" data-testid="public-book-continue">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Devam"}
            </Button>
          </>
        )}
        {step === "confirm" && (
          <>
            <div className="rounded-xl border border-stone-200 p-3" data-testid="public-book-match">
              <p className="text-xs text-stone-500">Bu numara şu kişiye kayıtlı:</p>
              <p className="text-lg font-bold text-[#1F2A44]" data-testid="public-book-masked-name">{match?.masked_name} — Siz misiniz?</p>
              <div className="flex gap-2 mt-2">
                <span className="text-xs rounded-lg bg-emerald-100 text-emerald-800 px-2 py-1 font-medium">✓ Evet, benim</span>
                <button type="button" onClick={() => { setStep("phone"); setError("Numaranızı kontrol edin. Numara doğruysa ve bu kişi siz değilseniz lütfen stüdyoyu arayın."); }}
                  className="text-xs underline text-stone-600" data-testid="public-book-not-me">Hayır, ben değilim</button>
              </div>
            </div>
            {Payment}
            {error && <p className="text-sm text-red-600" data-testid="public-book-error">{error}</p>}
            <Button onClick={submit} disabled={busy || !ready} className="w-full h-12 rounded-xl bg-[#1F2A44] hover:bg-[#1F2A44]/90 text-base" data-testid="public-book-submit">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : `Evet, benim — ${okLabel}`}
            </Button>
          </>
        )}
        {step === "new" && (
          <>
            <p className="text-sm text-stone-600" data-testid="public-book-new-info">İlk kez geliyorsunuz, hoş geldiniz! Lütfen bilgilerinizi girin.</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Ad *</Label><Input value={form.name} onChange={set("name")} className="rounded-xl" data-testid="public-book-name" /></div>
              <div><Label>Soyad *</Label><Input value={form.surname} onChange={set("surname")} className="rounded-xl" data-testid="public-book-surname" /></div>
            </div>
            <div>
              <Label>Cinsiyet *</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {[["female", "Kadın"], ["male", "Erkek"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setForm((f) => ({ ...f, gender: v }))}
                    className={`h-10 rounded-xl border text-sm font-medium transition-colors ${form.gender === v ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-stone-200 hover:border-stone-400"}`}
                    data-testid={`public-book-gender-${v}`}>{l}</button>
                ))}
              </div>
            </div>
            <div><Label>Doğum Tarihi *</Label><DateInputTR value={form.birth_date} onChange={set("birth_date")} maxDate={new Date()} className="rounded-xl w-full" placeholder="Doğum tarihinizi seçin" data-testid="public-book-birth-date" /></div>
            <div><Label>E-posta <span className="text-stone-400 font-normal">(isteğe bağlı)</span></Label><Input type="email" inputMode="email" value={form.email} onChange={set("email")} className="rounded-xl" placeholder="ornek@mail.com" data-testid="public-book-email" /></div>
            {Payment}
            {error && <p className="text-sm text-red-600" data-testid="public-book-error">{error}</p>}
            <Button onClick={submit} disabled={busy || !ready} className="w-full h-12 rounded-xl bg-[#1F2A44] hover:bg-[#1F2A44]/90 text-base" data-testid="public-book-submit">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : okLabel}
            </Button>
            <button type="button" onClick={() => setStep("phone")} className="text-xs underline text-stone-500" data-testid="public-book-back">Numarayı değiştir</button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const MyBookingsDialog = ({ open, onClose, token, onChanged }) => {
  const [phone, setPhone] = useState(localStorage.getItem("pb_phone") || "");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [confirmBooking, setConfirmBooking] = useState(null);

  const load = async () => {
    setError("");
    try { setData(await post(`${API}/public/booking/${token}/my-bookings`, { phone })); } catch (e) { setError(e.message); }
  };
  const leave = async (id) => {
    setBusyId(id);
    try { await post(`${API}/public/booking/${token}/waitlist/leave`, { waitlist_id: id, phone }); await load(); }
    catch (e) { setError(e.message); } finally { setBusyId(null); }
  };
  const cancel = async (b) => {
    setBusyId(b.reservation_id);
    try {
      await post(`${API}/public/booking/${token}/cancel`, { reservation_id: b.reservation_id, phone });
      setConfirmBooking(null);
      await load();
      onChanged();
    } catch (e) { setError(e.message); } finally { setBusyId(null); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent aria-describedby={undefined} className="max-w-md w-full md:w-[94vw] md:rounded-2xl content-start" data-testid="public-mybookings-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: "Manrope" }}>Rezervasyonlarım</DialogTitle></DialogHeader>
          <div className="flex gap-2">
            <Input type="tel" placeholder="Telefon numaranız" value={phone} onChange={(e) => setPhone(e.target.value)} className="rounded-xl" data-testid="public-mybookings-phone" />
            <Button onClick={load} className="rounded-xl bg-[#1F2A44]" data-testid="public-mybookings-search">Göster</Button>
          </div>
          {error && <p className="text-sm text-red-600" data-testid="public-mybookings-error">{error}</p>}
          {data && (
            <div className="space-y-2 max-h-[50vh] overflow-y-auto">
              {data.bookings.length === 0 && !(data.requests || []).length && !(data.waitlist || []).length && (
                <p className="text-sm text-stone-500 py-4 text-center" data-testid="public-mybookings-empty">Yaklaşan rezervasyon veya talep bulunamadı.</p>
              )}
              
              {/* Onay Bekleyen veya Reddedilen Talepler */}
              {(data.requests || []).map((r) => (
                <div key={r.request_id} className={`rounded-xl border p-3 flex items-center justify-between gap-2 ${r.status === "rejected" ? "border-red-200 bg-red-50/50" : "border-amber-200 bg-amber-50/60"}`}>
                  <div className="text-sm">
                    <p className="font-semibold text-[#1F2A44]">{dayLabel(r.date)}</p>
                    <p className="text-stone-600">{r.start_time}–{r.end_time} · {r.trainer_name}</p>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${r.status === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>
                    {r.status === "rejected" ? "Talep Reddedildi" : "Onay Bekliyor"}
                  </span>
                </div>
              ))}

              {data.bookings.map((b) => (
                <div key={b.reservation_id} className="rounded-xl border border-stone-200 p-3 flex items-center justify-between gap-2" data-testid={`public-mybooking-${b.reservation_id}`}>
                  <div className="text-sm">
                    <p className="font-semibold text-[#1F2A44]">{dayLabel(b.date)}</p>
                    <p className="text-stone-600">{b.start_time}–{b.end_time} · {b.trainer_name}</p>
                  </div>
                  {b.can_cancel ? (
                    <Button variant="outline" size="sm" disabled={busyId === b.reservation_id} onClick={() => setConfirmBooking(b)} className="rounded-lg text-red-600 border-red-200 hover:bg-red-50" data-testid={`public-cancel-${b.reservation_id}`}>
                      {busyId === b.reservation_id ? <Loader2 className="w-4 h-4 animate-spin" /> : "İptal Et"}
                    </Button>
                  ) : (
                    <span className="text-[11px] text-stone-500 text-right">İptal için<br />stüdyoyu arayın</span>
                  )}
                </div>
              ))}
              {(data.waitlist || []).map((w) => (
                <div key={w.waitlist_id} className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 flex items-center justify-between gap-2" data-testid={`public-mywaitlist-${w.waitlist_id}`}>
                  <div className="text-sm">
                    <p className="font-semibold text-[#1F2A44]">{dayLabel(w.date)} · Bekleme listesi</p>
                    <p className="text-stone-600">{w.start_time}–{w.end_time} · Sıranız: <b>{w.position}</b></p>
                  </div>
                  <Button variant="outline" size="sm" disabled={busyId === w.waitlist_id} onClick={() => leave(w.waitlist_id)} className="rounded-lg" data-testid={`public-leave-waitlist-${w.waitlist_id}`}>Listeden çık</Button>
                </div>
              ))}
              <p className="text-xs text-stone-500">Dersten en geç {data.cancel_hours} saat önce iptal edebilirsiniz.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Onay Diyaloğu (Ayrı Portal olarak dışarıda) */}
      <Dialog open={!!confirmBooking} onOpenChange={(o) => !o && setConfirmBooking(null)}>
        <DialogContent className="max-w-xs w-[90vw] rounded-2xl text-center p-5">
          <DialogHeader>
            <DialogTitle className="text-center text-lg">Rezervasyonu İptal Et</DialogTitle>
          </DialogHeader>
          {confirmBooking && (
            <p className="text-sm text-stone-600 my-2">
              <b>{dayLabel(confirmBooking.date)} ({confirmBooking.start_time})</b> tarihli rezervasyonunuzu iptal etmek istediğinize emin misiniz?
            </p>
          )}
          <div className="flex gap-2 mt-3">
            <Button variant="outline" onClick={() => setConfirmBooking(null)} className="flex-1 rounded-xl">Vazgeç</Button>
            <Button onClick={() => cancel(confirmBooking)} disabled={!!busyId} className="flex-1 rounded-xl bg-red-600 hover:bg-red-700 text-white">
              {busyId ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Evet, İptal Et"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default function PublicBookingPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [day, setDay] = useState(null);
  const [picked, setPicked] = useState(null);
  const [done, setDone] = useState(null);
  const [showMine, setShowMine] = useState(false);
  const [waitCls, setWaitCls] = useState(null);

  // Demo Talebi State'leri
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [demoForm, setDemoForm] = useState({ name: "", surname: "", phone: "", branch: "" });
  const [demoBranches, setDemoBranches] = useState([]);
  const [demoBusy, setDemoBusy] = useState(false);
  const [demoError, setDemoError] = useState("");

  // Branşları çekme işlemi (Token veya modal açıldığında tetiklenir)
  useEffect(() => {
    if (info) {
      // 1. İşletmenin verisinde branches dizisi varsa al
      let branches = info.branches || info.training_types || [];

      // 2. Ders kartlarından ve sınıflardan branşları ayıkla
      const classBranches = info.classes ? info.classes.map((c) => c.branch).filter(Boolean) : [];
      
      // Hepsini birleştir ve benzersiz yap
      const allBranches = [...new Set([...branches, ...classBranches])];
      
      setDemoBranches(allBranches);
    }
  }, [info]);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/public/booking/${token}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Link geçersiz");
      setInfo(data);
    } catch (e) { setError(e.message); }
  }, [token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // Kullanıcı bu sayfadayken "REZERVASYON" logosu aktif olsun
    const appleIcon = document.getElementById("app-touch-icon") || document.querySelector("link[rel='apple-touch-icon']");
    if (appleIcon) {
      appleIcon.setAttribute("href", "/icons/icon-rezervasyon-192x192.png");
    }

    // Sayfadan ayrılırsa varsayılan logoya geri dönsün
    return () => {
      if (appleIcon) {
        appleIcon.setAttribute("href", "/icons/icon-192x192.png");
      }
    };
  }, []);
  
  const days = info ? [...new Set(info.classes.map((c) => c.date))] : [];
  const activeDay = day && days.includes(day) ? day : days[0];
  const list = info ? info.classes.filter((c) => c.date === activeDay) : [];

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center p-6 text-center" data-testid="public-booking-error">
        <div><p className="text-xl font-bold text-[#1F2A44]" style={{ fontFamily: "Manrope" }}>Rezervasyon linki kullanılamıyor</p><p className="text-stone-600 mt-2">{error}</p></div>
      </div>
    );
  }
  if (!info) return <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>;

  return (
    <div className="min-h-screen bg-[#FAF7F2]" data-testid="public-booking-page">
      <header className="bg-[#1F2A44] text-white px-5 pt-8 pb-10 rounded-b-[2rem]">
        <div className="max-w-xl mx-auto flex items-center gap-4">
          {info.logo && <img src={info.logo} alt="" className="w-14 h-14 rounded-2xl object-cover bg-white" />}
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.2em] text-emerald-300">Online Ders Rezervasyonu</p>
            <h1 className="text-2xl sm:text-3xl font-extrabold truncate" style={{ fontFamily: "Manrope" }} data-testid="public-business-name">{info.business_name}</h1>
            {info.address && <p className="text-xs text-white/70 mt-1 flex items-center gap-1 truncate"><MapPin className="w-3 h-3 shrink-0" /> {info.address}</p>}
          </div>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 -mt-6 pb-16 space-y-4">
        {/* Ücretsiz Demo Ders Talebi Butonu */}
        <div className="bg-gradient-to-r from-emerald-700 to-teal-800 rounded-2xl p-4 text-white shadow-sm flex items-center justify-between gap-3">
          <div>
            <p className="font-bold text-base flex items-center gap-1.5" style={{ fontFamily: "Manrope" }}>
              <Sparkles className="w-5 h-5 text-amber-300" /> Ücretsiz Demo Ders Talebi
            </p>
            <p className="text-xs text-white/80 mt-0.5">
              Hangi dersin size uygun olduğunu öğrenmek için hemen talep bırakın!
            </p>
          </div>
          <Button
            onClick={() => setShowDemoModal(true)}
            className="bg-white text-emerald-900 hover:bg-emerald-50 rounded-xl font-bold shrink-0 text-xs sm:text-sm h-10 px-4"
          >
            Talep Oluştur
          </Button>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-2 flex gap-2 overflow-x-auto" data-testid="public-day-tabs">
          {days.map((d) => {
            const dt = parse(d);
            const on = d === activeDay;
            return (
              <button key={d} onClick={() => setDay(d)} className={`shrink-0 rounded-xl px-3 py-2 text-center min-w-[64px] transition-colors ${on ? "bg-emerald-600 text-white" : "hover:bg-stone-100 text-[#1F2A44]"}`} data-testid={`public-day-${d}`}>
                <p className="text-[11px] opacity-80">{DAY[dt.getDay()]}</p>
                <p className="text-lg font-bold leading-tight" style={{ fontFamily: "Manrope" }}>{dt.getDate()}</p>
                <p className="text-[10px] opacity-80">{MON[dt.getMonth()]}</p>
              </button>
            );
          })}
          {days.length === 0 && <p className="text-sm text-stone-500 p-3" data-testid="public-no-classes">Önümüzdeki 7 gün için açık ders yok.</p>}
        </div>

        {activeDay && <p className="text-sm font-semibold text-stone-600 flex items-center gap-1.5 px-1"><CalendarDays className="w-4 h-4" /> {dayLabel(activeDay)}</p>}
        <div className="space-y-3">{list.map((c) => <ClassCard key={c.class_id} c={c} onPick={setPicked} onWaitlist={setWaitCls} />)}</div>

        <button onClick={() => setShowMine(true)} className="w-full rounded-2xl border border-dashed border-stone-300 py-3 text-sm text-stone-700 hover:bg-white transition-colors flex items-center justify-center gap-2" data-testid="public-open-mybookings">
          <Phone className="w-4 h-4" /> Rezervasyonlarım / İptal
        </button>
        {info.phone && <p className="text-center text-xs text-stone-500">Sorularınız için: <a href={`tel:${info.phone}`} className="underline">{info.phone}</a></p>}
      </main>

      <BookingDialog cls={picked} cards={info.cards} token={token} onClose={() => setPicked(null)}
        onBooked={(r) => { setDone({ ...r, branch: picked?.branch, class_type: picked?.class_type }); setPicked(null); load(); }} />
      <BookingDialog waitlist cls={waitCls} cards={info.cards} token={token} onClose={() => setWaitCls(null)}
        onBooked={(r) => { setWaitCls(null); toast.success(`Bekleme listesine eklendiniz. Sıranız: ${r.position}`); }} />
      <MyBookingsDialog open={showMine} onClose={() => setShowMine(false)} token={token} onChanged={load} />

      <Dialog open={!!done} onOpenChange={(o) => !o && setDone(null)}>
        <DialogContent aria-describedby={undefined} className="max-w-sm w-[90vw] rounded-2xl text-center" data-testid="public-booking-success">
          <DialogTitle className="sr-only">Rezervasyon veya Talep Durumu</DialogTitle>
          
          {done?.status === "pending_approval" ? (
            <>
              <Clock className="w-14 h-14 text-amber-500 mx-auto" />
              <p className="text-xl font-bold text-[#1F2A44]" style={{ fontFamily: "Manrope" }}>Talebiniz Alındı!</p>
              <p className="text-sm text-stone-600 mt-1">İşletmemize ilk kez kayıt oluyorsunuz. Ders ve üyelik talebiniz onaylanmak üzere stüdyoya iletilmiştir.</p>
              {done && <p className="text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-2">{dayLabel(done.date)} · {done.start_time}–{done.end_time}</p>}
              <p className="text-xs text-stone-500 mt-1">Durumunuzu bu sayfadaki "Rezervasyonlarım" bölümünden takip edebilirsiniz.</p>
            </>
          ) : (
            <>
              <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto" />
              <p className="text-xl font-bold text-[#1F2A44]" style={{ fontFamily: "Manrope" }}>Yeriniz ayrıldı!</p>
              {done && <p className="text-stone-600">{dayLabel(done.date)} · {done.start_time}–{done.end_time}</p>}
              {done && !done.package_found && <p className="text-xs text-amber-700 bg-amber-50 rounded-lg p-2">Yeriniz ayrıldı. Paketinizi derse geldiğinizde stüdyodan alabilirsiniz.</p>}
              <p className="text-xs text-stone-500">İptal için en geç {info.cancel_hours} saat önce bu sayfadaki "Rezervasyonlarım" bölümünü kullanın.</p>
              {done && (
                <InviteFriendButton testId="public-success-invite" className="h-11 w-full"
                  businessName={info.business_name} lesson={[done.branch, done.class_type].filter(Boolean).join(" ")} date={done.date} startTime={done.start_time} endTime={done.end_time}
                  link={window.location.href.split("?")[0]} />
              )}
            </>
          )}

          <Button onClick={() => setDone(null)} variant="outline" className="rounded-xl" data-testid="public-success-close">Tamam</Button>
        </DialogContent>
      </Dialog>
      {/* Demo Ders Talebi Modalı */}
      {showDemoModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div 
            className="w-full max-w-md bg-white rounded-2xl p-5 sm:p-6 shadow-2xl relative max-h-[90vh] overflow-y-auto my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-3 border-b border-stone-100">
              <h3 style={{ fontFamily: "Manrope" }} className="flex items-center gap-2 text-lg font-bold text-[#1F2A44]">
                <Sparkles className="w-5 h-5 text-emerald-600" /> Demo Ders Talebi
              </h3>
              <button 
                onClick={() => setShowDemoModal(false)}
                className="text-stone-400 hover:text-stone-600 text-xl font-bold px-2 py-1"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4 mt-3">
              <p className="text-sm text-stone-600">
                İletişim bilgilerinizi bırakın, işletmemiz en kısa sürede sizi arayarak bilgilendirsin.
              </p>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Ad *</Label>
                  <Input
                    placeholder="Adınız"
                    value={demoForm.name}
                    onChange={(e) => setDemoForm({ ...demoForm, name: e.target.value })}
                    className="rounded-xl mt-1"
                  />
                </div>
                <div>
                  <Label>Soyad *</Label>
                  <Input
                    placeholder="Soyadınız"
                    value={demoForm.surname}
                    onChange={(e) => setDemoForm({ ...demoForm, surname: e.target.value })}
                    className="rounded-xl mt-1"
                  />
                </div>
              </div>

              <div>
                <Label>Cep Telefonu *</Label>
                <Input
                  type="tel"
                  placeholder="05xx xxx xx xx"
                  value={demoForm.phone}
                  onChange={(e) => setDemoForm({ ...demoForm, phone: e.target.value })}
                  className="rounded-xl mt-1"
                />
              </div>

              <div>
                <Label>İlgilendiğiniz Branş (İsteğe bağlı)</Label>
                <select
                  value={demoForm.branch}
                  onChange={(e) => setDemoForm({ ...demoForm, branch: e.target.value })}
                  className="w-full h-11 px-3 mt-1 rounded-xl border border-stone-200 bg-white text-sm text-[#1F2A44] focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Branş Seçiniz...</option>
                  {demoBranches && demoBranches.length > 0 ? (
                    demoBranches.map((b) => {
                      const branchName = typeof b === "object" ? (b.name || b.title) : b;
                      return (
                        <option key={branchName} value={branchName}>
                          {branchName}
                        </option>
                      );
                    })
                  ) : (
                    <option value="" disabled>Tanımlı branş bulunamadı</option>
                  )}
                </select>
              </div>

              {demoError && <p className="text-sm text-red-600 font-medium">{demoError}</p>}

              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowDemoModal(false)}
                  className="flex-1 h-12 rounded-xl border-stone-200 text-stone-700"
                >
                  Vazgeç / Kapat
                </Button>
                <Button
                  type="button"
                  disabled={demoBusy}
                  onClick={async () => {
                    setDemoError("");
                    if (!demoForm.name.trim() || !demoForm.surname.trim()) {
                      return setDemoError("Lütfen ad ve soyadınızı girin.");
                    }
                    if (!phoneOk(demoForm.phone)) {
                      return setDemoError("Geçerli bir cep telefonu girin (5xx xxx xx xx).");
                    }

                    setDemoBusy(true);
                    try {
                      // BURA DÜZELTİLDİ: info?.business_id veya info?.id olarak business_id garantilendi
                      const businessId = info?.business_id || info?.id; 

                      const res = await post(`${API}/reservations/demo-request`, {
                        business_id: info?.business_id || info?.id || null,
                        token: token, // URL'deki token doğrudan gönderiliyor
                        name: demoForm.name.trim(),
                        surname: demoForm.surname.trim(),
                        phone: demoForm.phone.trim(),
                        branch: demoForm.branch || null
                      });

                      toast.success(res.message || "Demo ders talebiniz başarıyla iletildi!");
                      setShowDemoModal(false);
                      setDemoForm({ name: "", surname: "", phone: "", branch: "" });
                    } catch (err) {
                      setDemoError(err.message);
                    } finally {
                      setDemoBusy(false);
                    }
                  }}
                  className="flex-1 h-12 rounded-xl bg-[#1F2A44] hover:bg-[#1F2A44]/90 text-white text-base"
                >
                  {demoBusy ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Talebi Gönder"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
